'use client';

import { isMetadataName, isMetadataSymbol, utf8ByteLength } from '@morfi/contract-sdk';

// T028 — Zustand store for multi-step proposal creation form with persist.
//
// Spec 007 cleanup: drafts are keyed PER-FLOW. Manual and Generate are two
// independent drafts — switching tabs no longer wipes the other side's
// progress. Implemented as two separate Zustand store instances with
// distinct localStorage keys (`morfi-proposal-form:manual` and
// `morfi-proposal-form:ai`). The active store is selected via React
// context (`<ProposalFormStoreProvider flow={...}>`), so all existing
// callsites of `useProposalFormStore(...)` keep working unchanged — they
// read from whichever store the provider currently exposes.

import type { JSX } from 'react';
import {
  type Context,
  createContext,
  createElement,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import { type StateCreator, type StoreApi, type UseBoundStore, useStore } from 'zustand';
import { createJSONStorage, type PersistOptions, persist } from 'zustand/middleware';
import { PROPOSAL_FIELDS } from '@/config/proposal-fields';
import { safeLocalStorage } from '@/lib/browser/safe-local-storage';
import { createTrackedStore } from '@/lib/dev/zustand-registry';
import { logger } from '@/lib/telemetry/logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MarketImageDraft {
  readonly croppedFile: File;
  readonly previewUrl: string;
  /** Durable, JSON-safe copy of the cropped bytes (a bounded WebP data-URI for
   *  manual picks, or the inline data-URI for AI drafts) written to localStorage
   *  by `partialize` and reconstructed into a `File` on rehydrate. Absent when
   *  the environment couldn't encode it; the draft then won't survive a reload,
   *  but the in-session pick is unaffected (MOR-847). */
  readonly persistedBytes?: string | undefined;
}

interface OutcomeEntry {
  /** Stable client-side id for React keys. Generated at creation; never sent to backend. */
  id: string;
  label: string;
  ticker: string;
  imageUrl: string;
  /** True while an optimistic upload is in flight. The visible imageUrl
   *  may be a transient blob: URL during this window; submission must
   *  block until this flips false. */
  imageUploading: boolean;
  /** Tiny base64 data-URI placeholder (LQIP) from the AI generator, painted
   *  instantly as a blur while the real `imageUrl` loads. Session-only. */
  imageLqip?: string | undefined;
  /** Moderate-res base64 data-URI of the AI-generated image — the bytes the
   *  client DISPLAYS during review and later submits through the deferred
   *  market bulk upload. Session-only (never persisted). */
  imagePreview?: string | undefined;
  /** Locally cropped manual/search/generated market image kept until create. */
  imageDraft: MarketImageDraft | null;
  /** Persistence carrier: the durable data-URI `partialize` writes and
   *  `restoreDraftsFromPersistedBytes` reads back on rehydrate to reconstruct
   *  `imageDraft`. Transient at runtime (cleared right after reconstruction);
   *  the runtime source of truth for a pending image is always `imageDraft`. */
  imageDraftBytes?: string | undefined;
}

function newOutcomeId(): string {
  // crypto.randomUUID is available in all browsers we target (and Node 19+ for tests).
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `o-${Math.random().toString(36).slice(2)}-${String(Date.now())}`;
}

function draftFromInlinePreview(
  previewUrl: string | undefined,
  fileNameStem: string,
): MarketImageDraft | null {
  if (previewUrl === undefined || typeof File === 'undefined') return null;
  const match = /^data:([^;,]+)(;base64)?,(.*)$/u.exec(previewUrl);
  if (match === null) return null;
  const contentType = match[1];
  const isBase64 = match[2] === ';base64';
  const payload = match[3];
  if (contentType === undefined || payload === undefined) return null;

  try {
    const binary = isBase64 ? atob(payload) : decodeURIComponent(payload);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const extension = extensionForImageContentType(contentType);
    if (extension === null) return null;
    return {
      croppedFile: new File([bytes], `${fileNameStem}.${extension}`, { type: contentType }),
      previewUrl,
      // `previewUrl` is a data-URI here (the regex above rejects blob:), so it
      // doubles as the durable persisted copy, so AI pin-failed drafts and
      // rehydrated drafts both survive the next reload (MOR-847).
      persistedBytes: previewUrl,
    };
  } catch {
    // SILENT-CATCH: invalid inline preview data falls back to the durable URL path.
    return null;
  }
}

function extensionForImageContentType(contentType: string): 'jpg' | 'png' | 'webp' | null {
  if (contentType === 'image/jpeg') return 'jpg';
  if (contentType === 'image/png') return 'png';
  if (contentType === 'image/webp') return 'webp';
  return null;
}

/** Rehydrate one outcome from its persisted `imageDraftBytes` carrier: rebuild
 *  the `File` + display preview, clear the carrier, and reset the in-flight
 *  flag. A no-op (beyond flag/carrier cleanup) when the slot already has a draft
 *  or no persisted bytes. See `restoreDraftsFromPersistedBytes` (MOR-847). */
function reconstructOutcomeDraft(outcome: OutcomeEntry, index: number): OutcomeEntry {
  if (outcome.imageDraft !== null || typeof outcome.imageDraftBytes !== 'string') {
    return outcome.imageUploading || outcome.imageDraftBytes !== undefined
      ? { ...outcome, imageUploading: false, imageDraftBytes: undefined }
      : outcome;
  }
  const draft = draftFromInlinePreview(outcome.imageDraftBytes, `draft-outcome-${String(index)}`);
  if (draft === null) {
    logger.warn('proposal-form.draft_reconstruct_failed', { slot: `outcome-${String(index)}` });
    return { ...outcome, imageUploading: false, imageDraftBytes: undefined };
  }
  return {
    ...outcome,
    imageDraft: draft,
    imagePreview: draft.previewUrl,
    imageUploading: false,
    imageDraftBytes: undefined,
  };
}

/** Rehydrate the cover from its persisted `coverImageDraftBytes` carrier
 *  (the cover counterpart to `reconstructOutcomeDraft`). Returns the cover-slice
 *  state patch (always clears the carrier + in-flight flag). */
function reconstructCoverDraft(state: ProposalFormState): Partial<ProposalFormState> {
  const base: Partial<ProposalFormState> = {
    coverImageUploading: false,
    coverImageDraftBytes: undefined,
  };
  if (state.coverImageDraft !== null || typeof state.coverImageDraftBytes !== 'string') {
    return base;
  }
  const draft = draftFromInlinePreview(state.coverImageDraftBytes, 'draft-cover');
  if (draft === null) {
    logger.warn('proposal-form.draft_reconstruct_failed', { slot: 'cover' });
    return base;
  }
  return {
    ...base,
    coverImageDraft: draft,
    coverImagePreview: draft.previewUrl,
  };
}

/** Total-size cap (in data-URI chars ≈ storage cost) on the persisted draft
 *  image bytes, leaving headroom under the ~5MB localStorage quota for the text
 *  fields. Bounded manual WebP drafts sit far below this; the cap mainly guards
 *  against many large AI-preview data-URIs. */
const MAX_TOTAL_PERSISTED_DRAFT_CHARS = 3_500_000;

/** Enforce `MAX_TOTAL_PERSISTED_DRAFT_CHARS` across the cover + outcome byte
 *  carriers by dropping the largest first, so an oversize set degrades to
 *  "text + smaller images persist" instead of failing the whole write. Logs
 *  once when it drops anything. Operates on the already-mapped persisted
 *  outcomes (does not touch runtime state). */
function trimPersistedDraftBytes(
  coverBytes: string | undefined,
  outcomes: OutcomeEntry[],
): { coverImageDraftBytes: string | undefined; outcomes: OutcomeEntry[] } {
  const nextOutcomes = outcomes.map((o) => ({ ...o }));
  let cover = coverBytes;
  const dropped: string[] = [];

  const total = (): number =>
    (cover?.length ?? 0) +
    nextOutcomes.reduce((sum, o) => sum + (o.imageDraftBytes?.length ?? 0), 0);

  while (total() > MAX_TOTAL_PERSISTED_DRAFT_CHARS) {
    let largestSize = 0;
    let largestIdx = -2; // -2 = nothing left, -1 = cover, >=0 = outcome index
    if (cover !== undefined && cover.length > largestSize) {
      largestSize = cover.length;
      largestIdx = -1;
    }
    for (let i = 0; i < nextOutcomes.length; i++) {
      const size = nextOutcomes[i]?.imageDraftBytes?.length ?? 0;
      if (size > largestSize) {
        largestSize = size;
        largestIdx = i;
      }
    }
    if (largestIdx === -2) break;
    if (largestIdx === -1) {
      cover = undefined;
      dropped.push('cover');
    } else {
      const target = nextOutcomes[largestIdx];
      if (target !== undefined) target.imageDraftBytes = undefined;
      dropped.push(`outcome-${String(largestIdx)}`);
    }
  }

  if (dropped.length > 0) {
    logger.warn('proposal-form.persist_image_trimmed', {
      dropped,
      budget: MAX_TOTAL_PERSISTED_DRAFT_CHARS,
    });
  }
  return { coverImageDraftBytes: cover, outcomes: nextOutcomes };
}

/** Per-step validation error maps. Keys are field names, values are i18n error key + params. */
interface StepValidationError {
  key: string;
  params?: Record<string, string | number>;
}

type StepErrors = Record<string, StepValidationError>;

// ---------------------------------------------------------------------------
// AI streaming — ephemeral status only (spec 007)
//
// AI-generated proposal data is now streamed directly into the regular form
// fields (title, description, category, location, coverImageUrl, outcomes)
// so the multi-step proposal creation flow updates live as each SSE event
// arrives. Only the stream STATUS + error envelope live here as ephemeral
// state — they are not persisted (excluded from `partialize`).
// ---------------------------------------------------------------------------

export type AiStreamStatus = 'idle' | 'streaming' | 'done' | 'error';
export type AiCreateReadyStatus = 'idle' | 'waiting' | 'ready' | 'failed' | 'cancelled';
export type MarketRequestKind = 'opinion' | 'prediction';
export type AiErrorCode = 'network' | 'rate_limited' | 'ai_market_content_policy';

export interface AiError {
  stage: string;
  message: string;
  retryable: boolean;
  /** Pre-flight network failure marker (EC-16) — `code: 'network'` indicates
   *  the fetch rejected before any SSE frame arrived. Distinguishable from
   *  mid-stream errors (which set `stage` to a module name). The
   *  `'rate_limited'` value (FR-029, T168) marks a 429 with the
   *  `details.reset` parsed into `resetMs` / `retryAfterSeconds`. */
  code?: AiErrorCode;
  /** Spec 007 / T168 — when `code === 'rate_limited'`, the unix-ms timestamp
   *  parsed from the AI envelope's `details.reset` field. */
  resetMs?: number;
  /** Spec 007 / T168 — `Retry-After` value in seconds (ceil from
   *  `resetMs - Date.now()`), exposed for components that want to render
   *  inline countdown affordances. */
  retryAfterSeconds?: number;
}

export interface ProposalFormState {
  /** Current form step. Spec 113 unified flow: 1=Name, 2=Details, 3=Preview, 4=Fund. */
  step: number;
  /** Epoch-ms of the last content mutation. Stamped inside the mutation
   *  actions (never read via Date.now() in render — constitution §12.3).
   *  Drives step-1 draft selection when both per-flow drafts exist
   *  (spec 113 FR-019). */
  lastTouchedAt: number;
  title: string;
  marketTicker: string;
  description: string;
  category: string;
  location: string;
  coverImageUrl: string;
  coverImageUploading: boolean;
  /** Tiny base64 LQIP placeholder for the cover from the AI generator —
   *  painted instantly as a blur while `coverImageUrl` loads. Session-only. */
  coverImageLqip?: string | undefined;
  /** Moderate-res base64 data-URI of the AI cover image — the bytes the client
   *  DISPLAYS during review and later submits through the deferred market bulk
   *  upload. Session-only. */
  coverImagePreview?: string | undefined;
  /** Locally cropped cover image kept until create. Session-only. */
  coverImageDraft: MarketImageDraft | null;
  /** Persistence carrier for the cover draft. See `OutcomeEntry.imageDraftBytes`. */
  coverImageDraftBytes?: string | undefined;
  outcomes: OutcomeEntry[];
  fundOutcomeIndex: number;
  fundAmount: string;

  /**
   * On-chain outcome floor/ceiling for the default `MarketConfig`. Sourced
   * from `useGetPlatformConfig({ kind: 'outcome' })` (via `useOutcomesLimits`)
   * at mount time and pushed in via
   * `setOutcomeLimits` so `validateStep3` can enforce the chain truth
   * instead of a hardcoded constant. `null` while the hook is loading or
   * errored — callers gate destructive actions on the resolved value.
   */
  outcomeLimits: { min: number; max: number } | null;

  /** Tracks which steps the user has attempted to advance past. */
  stepsAttempted: Set<number>;

  // ---- AI streaming ephemeral state (spec 007) — never persisted -----------
  aiStreamStatus: AiStreamStatus;
  aiError: AiError | null;
  /** How many image fetches we expect for this generation (1 cover + N outcomes).
   *  Set to a non-zero value once the `outcomes.done` SSE event has arrived. Drives
   *  the `Finding images (X/N)` progress chip in the AI edit form. */
  aiImagesExpected: number;
  /** How many of those image fetches have settled (locked OR failed). */
  aiImagesSettled: number;
  aiJobId: string | null;
  aiLastSeq: number;
  aiCreateReadyStatus: AiCreateReadyStatus;
  aiCancelRequested: boolean;
  /** Latest jev verdict for the draft, including edits reclassified before Review. */
  aiMarketKind: MarketRequestKind | null;
  setAiStreamStatus: (status: AiStreamStatus) => void;
  setAiError: (err: AiError | null) => void;
  setAiImagesExpected: (n: number) => void;
  setAiImagesSettled: (n: number) => void;
  incrementAiImagesSettled: () => void;
  setAiJobId: (jobId: string | null) => void;
  setAiLastSeq: (seq: number) => void;
  setAiCreateReadyStatus: (status: AiCreateReadyStatus) => void;
  setAiCancelRequested: (requested: boolean) => void;
  setAiMarketKind: (kind: MarketRequestKind | null) => void;
  /** Clear the AI-targeted form fields (title, marketTicker, description,
   *  category, location, coverImageUrl, coverImageUploading) and replace
   *  outcomes with two fresh defaults. Leaves `step`, `fundOutcomeIndex`,
   *  and `fundAmount` untouched (those belong to step 5). Called by the
   *  SSE hook at the start of a new generation. */
  resetForAiGeneration: () => void;
  // -------------------------------------------------------------------------

  // Actions
  setStep: (step: number) => void;
  updateField: <K extends keyof ProposalFormFields>(field: K, value: ProposalFormFields[K]) => void;
  addOutcome: () => void;
  removeOutcome: (index: number) => void;
  updateOutcomeLabel: (index: number, label: string) => void;
  updateOutcomeTicker: (index: number, ticker: string) => void;
  updateOutcomeImageUrl: (index: number, imageUrl: string) => void;
  setOutcomeImageDraft: (index: number, draft: MarketImageDraft) => void;
  setOutcomeImage: (index: number, imageUrl: string, uploading: boolean, lqip?: string) => void;
  setCoverImageDraft: (draft: MarketImageDraft) => void;
  setCoverImage: (url: string, uploading: boolean, lqip?: string) => void;
  // AI image lifecycle: generation now applies durable pinned URLs directly.
  // Preview setters are retained for legacy state restoration/tests; streamed
  // `*.locked` frames no longer call them.
  setCoverImagePreview: (preview: string, lqip?: string) => void;
  setCoverImagePinned: (url: string) => void;
  setCoverImagePinFailed: () => void;
  setOutcomeImagePreview: (index: number, preview: string, lqip?: string) => void;
  setOutcomeImagePinned: (index: number, url: string) => void;
  setOutcomeImagePinFailed: (index: number) => void;
  /** Clear every in-flight image-upload flag (cover + all outcomes),
   *  leaving each `imageUrl` untouched. Called when the AI stream
   *  terminates (done / error / cancel): once the stream is over, no
   *  further `*.pinned` / `*.pin_failed` frame will arrive, so an image
   *  left in the `locked` state (a dropped terminal frame, an aborted
   *  pin, or a stream torn down mid-pin) would otherwise strand
   *  `imageUploading: true` forever and permanently disable the
   *  "Create Proposal" button. */
  clearInFlightImageUploads: () => void;
  /** Rebuild the in-memory `MarketImageDraft` (File) + display preview for the
   *  cover and every outcome from the persisted `*ImageDraftBytes` carriers,
   *  then clear those carriers and any in-flight upload flags. Runs once on
   *  rehydrate so a reloaded draft's images reappear (display) and re-upload on
   *  submit (`imageDraft` restored). The MOR-847 fix. Idempotent. */
  restoreDraftsFromPersistedBytes: () => void;
  /** Replace the entire outcomes list with AI-suggested labels + tickers,
   *  preserving image URLs only for indices supplied via `imagesByIndex`.
   *  Used by the manual-flow `<SuggestOutcomesButton>` (spec 007 / T157). */
  replaceOutcomesFromAi: (
    suggested: { label: string; ticker: string }[],
    imagesByIndex?: Record<number, string>,
  ) => void;
  markStepAttempted: (step: number) => void;
  /** Push the resolved on-chain `min_outcomes`/`max_outcomes` into the
   *  store so `validateStep3` can enforce them. Pass `null` to clear when
   *  the source query errors. */
  setOutcomeLimits: (limits: { min: number; max: number } | null) => void;
  reset: () => void;
}

/** Subset of state fields that can be updated via updateField. */
interface ProposalFormFields {
  title: string;
  marketTicker: string;
  description: string;
  category: string;
  location: string;
  fundOutcomeIndex: number;
  fundAmount: string;
}

/** Serializable state for persist (excludes action functions). */
interface ProposalFormPersistedState {
  step: number;
  lastTouchedAt: number;
  title: string;
  marketTicker: string;
  description: string;
  category: string;
  location: string;
  coverImageUrl: string;
  coverImageUploading: boolean;
  /** Tiny base64 LQIP placeholder for the cover from the AI generator —
   *  painted instantly as a blur while `coverImageUrl` loads. Session-only. */
  coverImageLqip?: string | undefined;
  /** Durable cover-draft bytes (data-URI): the only image bytes we persist, so
   *  a picked-but-unuploaded cover survives a reload (MOR-847). */
  coverImageDraftBytes?: string | undefined;
  outcomes: OutcomeEntry[];
  fundOutcomeIndex: number;
  fundAmount: string;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

function defaultOutcomes(): OutcomeEntry[] {
  return [
    // ALLOW-NSFW-STRIP: draft-form state — moderation runs after upload, not in form state
    {
      id: newOutcomeId(),
      label: '',
      ticker: '',
      imageUrl: '',
      imageUploading: false,
      imageDraft: null,
    },
    // ALLOW-NSFW-STRIP: draft-form state — moderation runs after upload, not in form state
    {
      id: newOutcomeId(),
      label: '',
      ticker: '',
      imageUrl: '',
      imageUploading: false,
      imageDraft: null,
    },
  ];
}

function initialState(): ProposalFormPersistedState {
  return {
    step: 1,
    lastTouchedAt: 0,
    title: '',
    marketTicker: '',
    description: '',
    category: '',
    location: '',
    coverImageUrl: '',
    coverImageUploading: false,
    outcomes: defaultOutcomes(),
    fundOutcomeIndex: -1,
    fundAmount: '',
  };
}

function hasCompleteDurableImageUrls(state: ProposalFormState): boolean {
  const isDurableUrl = (url: string): boolean => url.length > 0 && !url.startsWith('blob:');

  return (
    isDurableUrl(state.coverImageUrl) &&
    state.outcomes.length > 0 &&
    state.outcomes.every((outcome) => isDurableUrl(outcome.imageUrl))
  );
}

// ---------------------------------------------------------------------------
// Step validation
// ---------------------------------------------------------------------------

function validateStep1Identity(state: ProposalFormPersistedState): StepErrors {
  const errors: StepErrors = {};

  if (state.title.trim().length === 0) {
    errors.title = { key: 'titleRequired' };
  } else if (state.title.trim().length < PROPOSAL_FIELDS.title.minLength) {
    errors.title = { key: 'titleTooShort', params: { min: PROPOSAL_FIELDS.title.minLength } };
  }

  const ticker = state.marketTicker;
  if (ticker.length === 0) {
    errors.marketTicker = { key: 'marketTickerRequired' };
  } else if (utf8ByteLength(ticker) < PROPOSAL_FIELDS.marketTicker.minLength) {
    errors.marketTicker = {
      key: 'marketTickerTooShort',
      params: { min: PROPOSAL_FIELDS.marketTicker.minLength },
    };
  } else if (
    !PROPOSAL_FIELDS.marketTicker.pattern.test(ticker) ||
    utf8ByteLength(ticker) > PROPOSAL_FIELDS.marketTicker.maxLength
  ) {
    errors.marketTicker = { key: 'marketTickerInvalid' };
  }

  return errors;
}

function validateStep1Description(state: ProposalFormPersistedState): StepErrors {
  // Description is required: it is baked into the on-chain editorial content
  // hash and message, and the backend create + editorial schemas both enforce
  // `min(1)`. Leaving it unvalidated let an empty description through to the
  // POST, which 422'd with "String must contain at least 1 character(s)"
  // (MOR-817).
  const errors: StepErrors = {};
  if (state.description.trim().length === 0) {
    errors.description = { key: 'descriptionRequired' };
  }
  return errors;
}

function validateStep1Classification(
  state: ProposalFormPersistedState,
  requireLocation: boolean = true,
): StepErrors {
  const errors: StepErrors = {};

  if (state.category.length === 0) {
    errors.category = { key: 'categoryRequired' };
  }

  if (requireLocation && state.location.length === 0) {
    errors.location = { key: 'locationRequired' };
  }

  return errors;
}

/** Union of the three step-1 validators — used by the Generate flow's
 *  single combined edit form (FR-005) which still presents identity +
 *  description + classification on one screen. */
function validateStep1(
  state: ProposalFormPersistedState,
  requireLocation: boolean = true,
): StepErrors {
  return {
    ...validateStep1Identity(state),
    ...validateStep1Description(state),
    ...validateStep1Classification(state, requireLocation),
  };
}

function validateStep2(_state: ProposalFormPersistedState): StepErrors {
  // Cover image step — fully optional, nothing to validate.
  return {};
}

function validateStep3(
  state: ProposalFormPersistedState,
  outcomeLimits: { min: number; max: number } | null,
): StepErrors {
  const errors: StepErrors = {};

  // The min count is on-chain truth — read from `outcomeLimits` (sourced
  // via `useGetPlatformConfig({ kind: 'outcome' })`). While the hook is still loading the
  // gate is a no-op; the UI separately disables Continue until limits
  // resolve.
  if (outcomeLimits !== null) {
    const filledOutcomes = state.outcomes.filter((o) => o.label.trim().length > 0);
    if (filledOutcomes.length < outcomeLimits.min) {
      errors.outcomes = { key: 'outcomesTooFew', params: { min: outcomeLimits.min } };
    } else if (filledOutcomes.length > outcomeLimits.max) {
      errors.outcomes = { key: 'outcomesTooMany', params: { max: outcomeLimits.max } };
    }
  }

  // Per-outcome label + ticker checks. The shared cashtag budget
  // (`$MARKETXOUTCOME` <= tickerCombined.maxLength) is enforced here in
  // addition to the dynamic input maxLength — covers the case where the user
  // changes the market ticker after entering outcomes.
  const marketTickerLen = utf8ByteLength(state.marketTicker);
  const combinedBudget = PROPOSAL_FIELDS.tickerCombined.maxLength;
  for (let i = 0; i < state.outcomes.length; i++) {
    const outcome = state.outcomes[i];
    if (outcome === undefined) continue;
    if (outcome.label.trim().length === 0) {
      errors[`outcomeLabel_${String(i)}`] = { key: 'outcomeLabelRequired' };
    }
    if (outcome.label.length > 0 && !isMetadataName(outcome.label)) {
      errors[`outcomeLabel_${String(i)}`] = {
        key: 'outcomeLabelTooLong',
        params: { max: PROPOSAL_FIELDS.outcomes.labelMaxLength },
      };
    }
    const ticker = outcome.ticker;
    if (ticker.length === 0) {
      errors[`outcomeTicker_${String(i)}`] = { key: 'outcomeTickerRequired' };
    } else if (!isMetadataSymbol(ticker)) {
      errors[`outcomeTicker_${String(i)}`] = { key: 'outcomeTickerInvalid' };
    } else if (marketTickerLen + 1 + utf8ByteLength(ticker) > combinedBudget) {
      const maxOutcome = Math.max(0, combinedBudget - 1 - marketTickerLen);
      errors[`outcomeTicker_${String(i)}`] = {
        key: 'outcomeTickerCombinedTooLong',
        params: { max: maxOutcome },
      };
    }
  }

  // Duplicate label detection
  const seenLabel = new Map<string, number>();
  for (let i = 0; i < state.outcomes.length; i++) {
    const outcome = state.outcomes[i];
    if (outcome === undefined) continue;
    const normalized = outcome.label.trim().toLowerCase();
    if (normalized.length === 0) continue;
    const existing = seenLabel.get(normalized);
    if (existing !== undefined) {
      errors[`outcomeDuplicate_${String(existing)}`] = { key: 'outcomeDuplicate' };
      errors[`outcomeDuplicate_${String(i)}`] = { key: 'outcomeDuplicate' };
    } else {
      seenLabel.set(normalized, i);
    }
  }

  // Duplicate ticker detection
  const seenTicker = new Map<string, number>();
  for (let i = 0; i < state.outcomes.length; i++) {
    const outcome = state.outcomes[i];
    if (outcome === undefined) continue;
    const normalized = outcome.ticker.trim().toUpperCase();
    if (normalized.length === 0) continue;
    const existing = seenTicker.get(normalized);
    if (existing !== undefined) {
      errors[`outcomeTickerDuplicate_${String(existing)}`] = { key: 'outcomeTickerDuplicate' };
      errors[`outcomeTickerDuplicate_${String(i)}`] = { key: 'outcomeTickerDuplicate' };
    } else {
      seenTicker.set(normalized, i);
    }
  }

  return errors;
}

/** Compute step 1 (combined identity + description + classification) validation
 *  errors. Used by the Generate flow's single edit form. The manual flow's
 *  per-step calls go through the three dedicated selectors below. */
export function getStep1Errors(state: ProposalFormState): StepErrors {
  return validateStep1(state);
}

/** Compute step 1A (identity — title + ticker) validation errors. */
export function getStep1IdentityErrors(state: ProposalFormState): StepErrors {
  return validateStep1Identity(state);
}

/** Compute step 1B (description) validation errors. */
export function getStep1DescriptionErrors(state: ProposalFormState): StepErrors {
  return validateStep1Description(state);
}

/** Compute step 1C (classification — category + location) validation errors. */
export function getStep1ClassificationErrors(state: ProposalFormState): StepErrors {
  return validateStep1Classification(state);
}

/** Compute step 2 (cover image) validation errors. Always empty — step is optional. */
export function getStep2Errors(state: ProposalFormState): StepErrors {
  return validateStep2(state);
}

/** Compute step 3 (outcomes) validation errors from current state. */
export function getStep3Errors(state: ProposalFormState): StepErrors {
  return validateStep3(state, state.outcomeLimits);
}

/** Spec 113 — consolidated "Market details" step validator. The unified flow
 *  presents identity + description + classification + cover + outcomes on ONE
 *  screen (step 2), so its advance gate is the union of every field-level
 *  validator the manual flow previously ran across its separate steps. This is
 *  exactly the merge the Generate flow already ran on its 2→3 transition
 *  (`getStep1Errors` + `getStep3Errors`), so no validation rule is dropped or
 *  added — it is a regrouping (FR-006/FR-007/FR-008). */
export function getDetailsStepErrors(
  state: ProposalFormState,
  options: { requireLocation?: boolean } = {},
): StepErrors {
  return {
    ...validateStep1(state, options.requireLocation ?? true),
    ...validateStep3(state, state.outcomeLimits),
  };
}

/** Pre-built selectors for step-attempted checks (avoids creating new functions on each render). */
const STEP_ATTEMPTED_SELECTORS: Record<number, (state: ProposalFormState) => boolean> = {
  1: (state: ProposalFormState): boolean => state.stepsAttempted.has(1),
  2: (state: ProposalFormState): boolean => state.stepsAttempted.has(2),
  3: (state: ProposalFormState): boolean => state.stepsAttempted.has(3),
  4: (state: ProposalFormState): boolean => state.stepsAttempted.has(4),
  5: (state: ProposalFormState): boolean => state.stepsAttempted.has(5),
  6: (state: ProposalFormState): boolean => state.stepsAttempted.has(6),
  7: (state: ProposalFormState): boolean => state.stepsAttempted.has(7),
};

/** Returns a stable selector for whether step validation was attempted. */
export function selectStepAttempted(step: number): (state: ProposalFormState) => boolean {
  const selector = STEP_ATTEMPTED_SELECTORS[step];
  if (selector !== undefined) return selector;
  return (state: ProposalFormState): boolean => state.stepsAttempted.has(step);
}

export type { StepErrors, StepValidationError };

// ---------------------------------------------------------------------------
// Store factory (spec 007 cleanup — per-flow draft isolation)
// ---------------------------------------------------------------------------

export type ProposalFormBoundStore = UseBoundStore<StoreApi<ProposalFormState>>;

/** Persisted draft schema consumed by the auth-continuation compatibility check. */
export const PROPOSAL_FORM_PERSIST_VERSION: 8 = 8;

function createProposalFormStore(opts: {
  flow: 'manual' | 'generate';
  /** Display name for the dev tracker — keeps the two stores distinguishable in the registry. */
  trackedName: string;
  /** localStorage key — must be unique per flow (`morfi-proposal-form:manual` etc.). */
  persistKey: string;
  ephemeral?: boolean;
}): ProposalFormBoundStore {
  return createTrackedStore<ProposalFormState>(opts.trackedName)(
    configureProposalPersistence(
      (set) => ({
        ...initialState(),
        coverImageDraft: null,
        stepsAttempted: new Set<number>(),
        outcomeLimits: null,

        // ---- AI streaming ephemeral state (never persisted) ---------------
        aiStreamStatus: 'idle' as AiStreamStatus,
        aiError: null,
        aiImagesExpected: 0,
        aiImagesSettled: 0,
        aiJobId: null,
        aiLastSeq: 0,
        aiCreateReadyStatus: 'idle' as AiCreateReadyStatus,
        aiCancelRequested: false,
        aiMarketKind: null,

        setAiStreamStatus: (status: AiStreamStatus): void => {
          set({ aiStreamStatus: status });
        },

        setAiError: (err: AiError | null): void => {
          set({ aiError: err });
        },

        setAiImagesExpected: (n: number): void => {
          set({ aiImagesExpected: n });
        },

        setAiImagesSettled: (n: number): void => {
          set({ aiImagesSettled: n });
        },

        incrementAiImagesSettled: (): void => {
          set((state) => ({
            aiImagesSettled: Math.min(state.aiImagesExpected, state.aiImagesSettled + 1),
          }));
        },

        setAiJobId: (jobId: string | null): void => {
          set({ aiJobId: jobId });
        },

        setAiLastSeq: (seq: number): void => {
          set({ aiLastSeq: seq });
        },

        setAiCreateReadyStatus: (status: AiCreateReadyStatus): void => {
          set({ aiCreateReadyStatus: status });
        },

        setAiCancelRequested: (requested: boolean): void => {
          set({ aiCancelRequested: requested });
        },

        setAiMarketKind: (kind: MarketRequestKind | null): void => {
          set({ aiMarketKind: kind });
        },

        resetForAiGeneration: (): void => {
          set({
            lastTouchedAt: Date.now(),
            title: '',
            marketTicker: '',
            description: '',
            category: '',
            location: '',
            coverImageUrl: '',
            coverImageUploading: false,
            coverImageDraft: null,
            coverImageDraftBytes: undefined,
            // Session-only preview bytes from a PREVIOUS generation must be
            // cleared explicitly — the image slot prefers the preview over
            // `coverImageUrl`, so leaving it shows the old cover (plus a
            // derived pin-failed badge) until the new cover.locked lands.
            coverImagePreview: undefined,
            coverImageLqip: undefined,
            outcomes: defaultOutcomes(),
            stepsAttempted: new Set<number>(),
            aiImagesExpected: 0,
            aiImagesSettled: 0,
            aiJobId: null,
            aiLastSeq: 0,
            aiCreateReadyStatus: 'waiting',
            aiCancelRequested: false,
            aiMarketKind: null,
          });
        },
        // -------------------------------------------------------------------

        setStep: (step: number): void => {
          set({ step });
        },

        markStepAttempted: (step: number): void => {
          set((state) => {
            const next = new Set(state.stepsAttempted);
            next.add(step);
            return { stepsAttempted: next };
          });
        },

        setOutcomeLimits: (limits: { min: number; max: number } | null): void => {
          set({ outcomeLimits: limits });
        },

        updateField: <K extends keyof ProposalFormFields>(
          field: K,
          value: ProposalFormFields[K],
        ): void => {
          set({ [field]: value, lastTouchedAt: Date.now() });
        },

        addOutcome: (): void => {
          set((state) => ({
            lastTouchedAt: Date.now(),
            outcomes: [
              ...state.outcomes,
              // ALLOW-NSFW-STRIP: draft-form state — moderation runs after upload, not in form state
              {
                id: newOutcomeId(),
                label: '',
                ticker: '',
                imageUrl: '',
                imageUploading: false,
                imageDraft: null,
              },
            ],
          }));
        },

        removeOutcome: (index: number): void => {
          set((state) => ({
            lastTouchedAt: Date.now(),
            outcomes: state.outcomes.filter((_, i) => i !== index),
          }));
        },

        updateOutcomeLabel: (index: number, label: string): void => {
          set((state) => ({
            lastTouchedAt: Date.now(),
            outcomes: state.outcomes.map((o, i) => (i === index ? { ...o, label } : o)),
          }));
        },

        updateOutcomeTicker: (index: number, ticker: string): void => {
          set((state) => ({
            lastTouchedAt: Date.now(),
            outcomes: state.outcomes.map((o, i) => (i === index ? { ...o, ticker } : o)),
          }));
        },

        updateOutcomeImageUrl: (index: number, imageUrl: string): void => {
          set((state) => ({
            lastTouchedAt: Date.now(),
            outcomes: state.outcomes.map((o, i) =>
              i === index ? { ...o, imageUrl, imageDraft: null } : o,
            ),
          }));
        },

        setOutcomeImageDraft: (index: number, draft: MarketImageDraft): void => {
          set((state) => ({
            lastTouchedAt: Date.now(),
            outcomes: state.outcomes.map((o, i) =>
              i === index
                ? {
                    ...o,
                    imageUrl: '',
                    imageUploading: false,
                    imageLqip: undefined,
                    imagePreview: draft.previewUrl,
                    imageDraft: draft,
                  }
                : o,
            ),
          }));
        },

        setOutcomeImage: (
          index: number,
          imageUrl: string,
          uploading: boolean,
          lqip?: string,
        ): void => {
          set((state) => ({
            lastTouchedAt: Date.now(),
            outcomes: state.outcomes.map((o, i) =>
              i === index
                ? {
                    ...o,
                    imageUrl,
                    imageUploading: uploading,
                    imageLqip: lqip,
                    // Manual set (picker/clear) supersedes any AI preview.
                    imagePreview: undefined,
                    imageDraft: null,
                  }
                : o,
            ),
          }));
        },

        setCoverImageDraft: (draft: MarketImageDraft): void => {
          // ALLOW-NSFW-STRIP: local draft-form image — moderation runs after upload.
          set({
            lastTouchedAt: Date.now(),
            coverImageUrl: '',
            coverImageUploading: false,
            coverImageLqip: undefined,
            coverImagePreview: draft.previewUrl,
            coverImageDraft: draft,
          });
        },

        setCoverImage: (url: string, uploading: boolean, lqip?: string): void => {
          // ALLOW-NSFW-STRIP: draft-form state — moderation runs at create-proposal time, not in the form slice
          set({
            lastTouchedAt: Date.now(),
            coverImageUrl: url,
            coverImageUploading: uploading,
            coverImageLqip: lqip,
            // Manual set (picker/clear) supersedes any AI preview.
            coverImagePreview: undefined,
            coverImageDraft: null,
          });
        },

        setCoverImagePreview: (preview: string, lqip?: string): void => {
          set({
            coverImagePreview: preview,
            coverImageLqip: lqip,
            coverImageUrl: '',
            coverImageUploading: true,
            coverImageDraft: null,
          });
        },
        setCoverImagePinned: (url: string): void => {
          set({
            coverImageUrl: url,
            coverImageUploading: false,
            coverImageLqip: undefined,
            coverImagePreview: undefined,
            coverImageDraft: null,
          });
        },
        setCoverImagePinFailed: (): void => {
          // Couldn't obtain a durable URL. Preview still displays; submit stays
          // blocked (coverImageUrl empty) until the user re-picks.
          set({ coverImageUploading: false });
        },

        setOutcomeImagePreview: (index: number, preview: string, lqip?: string): void => {
          set((state) => ({
            outcomes: state.outcomes.map((o, i) =>
              i === index
                ? {
                    ...o,
                    imagePreview: preview,
                    imageLqip: lqip,
                    imageUrl: '',
                    imageUploading: true,
                    imageDraft: null,
                  }
                : o,
            ),
          }));
        },
        setOutcomeImagePinned: (index: number, url: string): void => {
          set((state) => ({
            outcomes: state.outcomes.map((o, i) =>
              i === index
                ? {
                    ...o,
                    imageUrl: url,
                    imageUploading: false,
                    imageLqip: undefined,
                    imagePreview: undefined,
                    imageDraft: null,
                  }
                : o,
            ),
          }));
        },
        setOutcomeImagePinFailed: (index: number): void => {
          set((state) => ({
            outcomes: state.outcomes.map((o, i) =>
              i === index ? { ...o, imageUploading: false } : o,
            ),
          }));
        },

        clearInFlightImageUploads: (): void => {
          set((state) => ({
            coverImageUploading: false,
            outcomes: state.outcomes.map((o) =>
              o.imageUploading ? { ...o, imageUploading: false } : o,
            ),
          }));
        },

        restoreDraftsFromPersistedBytes: (): void => {
          // Also subsumes `clearInFlightImageUploads` (both reconstructors reset
          // the in-flight flags), so the rehydrate path calls only this.
          set((state) => ({
            ...reconstructCoverDraft(state),
            outcomes: state.outcomes.map(reconstructOutcomeDraft),
          }));
        },

        replaceOutcomesFromAi: (
          suggested: { label: string; ticker: string }[],
          imagesByIndex?: Record<number, string>,
        ): void => {
          set((): { outcomes: OutcomeEntry[] } => ({
            outcomes: suggested.map(
              (o, i): OutcomeEntry => ({
                id: newOutcomeId(),
                label: o.label,
                ticker: o.ticker,
                // ALLOW-NSFW-STRIP: AI-suggested URL — moderation runs at create-proposal time, not in the form slice
                imageUrl: imagesByIndex?.[i] ?? '',
                imageUploading: false,
                imageDraft: null,
              }),
            ),
          }));
        },

        reset: (): void => {
          set({
            ...initialState(),
            // `initialState()` omits the optional session-only preview fields
            // and zustand `set` merges, so they must be cleared explicitly.
            coverImagePreview: undefined,
            coverImageLqip: undefined,
            coverImageDraft: null,
            coverImageDraftBytes: undefined,
            stepsAttempted: new Set<number>(),
            aiStreamStatus: 'idle',
            aiError: null,
            aiImagesExpected: 0,
            aiImagesSettled: 0,
            aiJobId: null,
            aiLastSeq: 0,
            aiCreateReadyStatus: 'idle',
            aiCancelRequested: false,
            aiMarketKind: null,
          });
        },
      }),
      {
        name: opts.persistKey,
        // Bumped to 7 with the spec-113 unified 4-step flow (Name → Details →
        // Preview → Fund). Persisted step numbers from the old 7-stage manual
        // layout no longer line up, so the migration resets `step` to 1 and
        // clears `stepsAttempted`. Field content is preserved — we keep the
        // user's title/ticker/etc. so they don't lose their draft. `lastTouchedAt`
        // defaults to 0 for pre-v7 drafts (spec 113 FR-019/DM-6).
        // Bumped to 8 with MOR-847: cover + outcome draft image bytes are now
        // persisted (as data-URIs) and reconstructed on rehydrate. Pre-v8 drafts
        // simply lack those carriers → no image to restore, no data loss.
        version: PROPOSAL_FORM_PERSIST_VERSION,
        // ALLOW-UNKNOWN: Zustand persist migrate callback signature requires unknown
        migrate: (persisted: unknown, _oldVersion: number): ProposalFormPersistedState => {
          if (persisted === null || typeof persisted !== 'object') {
            return initialState();
          }
          const raw = persisted as Record<string, unknown>;
          const init = initialState();
          const rawOutcomes = Array.isArray(raw.outcomes) ? raw.outcomes : init.outcomes;
          // ALLOW-UNKNOWN: persisted entries are untyped opaque blobs from older versions; narrow per field.
          const outcomes: OutcomeEntry[] = rawOutcomes.map((o: unknown): OutcomeEntry => {
            if (o === null || typeof o !== 'object') {
              return {
                id: newOutcomeId(),
                label: '',
                ticker: '',
                imageUrl: '',
                imageUploading: false,
                imageDraft: null,
              };
            }
            const entry = o as Record<string, unknown>;
            // Persisted blob: URLs are dead refs from a prior session — drop
            // them so the user re-uploads. Same idea applied to coverImageUrl
            // below.
            const persistedImageUrl = typeof entry.imageUrl === 'string' ? entry.imageUrl : '';
            return {
              id: typeof entry.id === 'string' && entry.id.length > 0 ? entry.id : newOutcomeId(),
              label: typeof entry.label === 'string' ? entry.label : '',
              ticker: typeof entry.ticker === 'string' ? entry.ticker : '',
              // ALLOW-NSFW-STRIP: draft-form rehydrate — moderation runs after upload, not in form state
              imageUrl: persistedImageUrl.startsWith('blob:') ? '' : persistedImageUrl,
              imageUploading: false,
              imageDraft: null,
              // Carry the persisted draft bytes forward; `onRehydrateStorage`
              // reconstructs `imageDraft` from them (MOR-847).
              imageDraftBytes:
                typeof entry.imageDraftBytes === 'string' ? entry.imageDraftBytes : undefined,
            };
          });
          return {
            // Always reset to step 1 on rehydrate — older drafts used a
            // different stage numbering that doesn't map to the spec-113
            // unified 4-step flow. Field content is preserved below.
            step: 1,
            // Preserve a prior last-touched stamp if present; else 0 so an
            // untouched migrated draft never out-ranks a freshly edited one.
            lastTouchedAt: typeof raw.lastTouchedAt === 'number' ? raw.lastTouchedAt : 0,
            title: typeof raw.title === 'string' ? raw.title : init.title,
            marketTicker:
              typeof raw.marketTicker === 'string' ? raw.marketTicker : init.marketTicker,
            description: typeof raw.description === 'string' ? raw.description : init.description,
            category: typeof raw.category === 'string' ? raw.category : init.category,
            location: typeof raw.location === 'string' ? raw.location : init.location,
            // ALLOW-NSFW-STRIP: draft-form rehydrate — moderation runs after upload, not in form state
            coverImageUrl:
              typeof raw.coverImageUrl === 'string' && !raw.coverImageUrl.startsWith('blob:')
                ? raw.coverImageUrl
                : init.coverImageUrl,
            // Always reset uploading flags on rehydrate — any in-flight
            // upload from a prior tab/session is gone, never to be heard
            // from again. Don't strand the UI in a "uploading…" state.
            coverImageUploading: false,
            coverImageDraftBytes:
              typeof raw.coverImageDraftBytes === 'string' ? raw.coverImageDraftBytes : undefined,
            outcomes,
            fundAmount: typeof raw.fundAmount === 'string' ? raw.fundAmount : init.fundAmount,
            fundOutcomeIndex:
              typeof raw.fundOutcomeIndex === 'number'
                ? raw.fundOutcomeIndex
                : init.fundOutcomeIndex,
          };
        },
        partialize: (state: ProposalFormState): ProposalFormPersistedState => {
          // Strip the session-only / non-serializable image fields (imagePreview
          // blob-URL, imageLqip, the File-bearing imageDraft) but keep the
          // durable draft BYTES: derive each `imageDraftBytes` from the draft's
          // `persistedBytes` so a picked-but-unuploaded image survives a reload
          // (MOR-847) and is reconstructed by `restoreDraftsFromPersistedBytes`.
          // Reset imageUploading for the same reason as coverImageUploading.
          const mappedOutcomes = state.outcomes.map(
            // ALLOW-NSFW-STRIP: draft-form partialize — moderation runs after upload, not in form state
            ({
              imagePreview: _p,
              imageLqip: _l,
              imageDraft,
              imageDraftBytes: _b,
              ...rest
            }): OutcomeEntry => ({
              ...rest,
              imageUploading: false,
              imageDraft: null,
              imageDraftBytes: imageDraft?.persistedBytes,
            }),
          );
          const trimmed = trimPersistedDraftBytes(
            state.coverImageDraft?.persistedBytes,
            mappedOutcomes,
          );
          return {
            step: state.step,
            lastTouchedAt: state.lastTouchedAt,
            title: state.title,
            marketTicker: state.marketTicker,
            description: state.description,
            category: state.category,
            location: state.location,
            // ALLOW-NSFW-STRIP: draft-form partialize — moderation runs after upload, not in form state
            coverImageUrl: state.coverImageUrl,
            // Never persist an in-flight upload flag. Any upload that was
            // running when this snapshot was written is gone the moment the
            // tab/session ends — persisting `true` would rehydrate a draft
            // that strands the "Create Proposal" button on the next load
            // (the `migrate` callback resets these too, but only runs on a
            // version bump — a same-version reload would otherwise keep the
            // stale `true`).
            coverImageUploading: false,
            coverImageDraftBytes: trimmed.coverImageDraftBytes,
            outcomes: trimmed.outcomes,
            fundOutcomeIndex: state.fundOutcomeIndex,
            fundAmount: state.fundAmount,
          };
        },
        // Tolerant storage so a `setItem` quota/private-mode failure degrades
        // silently (in-memory draft still updates) instead of throwing and
        // aborting the whole draft write; the backstop to partialize's
        // size trim. Mirrors `recent-searches.ts`.
        storage: createJSONStorage(() => safeLocalStorage),
        // Read-side reconstruction + reset. `restoreDraftsFromPersistedBytes`
        // rebuilds each `imageDraft` (File) + preview from the persisted bytes
        // (MOR-847) and subsumes the belt-and-braces in-flight-flag reset:
        // `partialize` strips those flags from new writes, but a draft persisted
        // by an older build still carries `imageUploading: true`, and `migrate`
        // only runs on a version bump, so this heals existing drafts on load.
        onRehydrateStorage:
          () =>
          (state: ProposalFormState | undefined): void => {
            state?.restoreDraftsFromPersistedBytes();
            // The server's create-ready event is session-only. A resumed AI
            // draft with every generated image pinned has already satisfied
            // that event's durable condition, so recover the submission gate.
            if (
              opts.flow === 'generate' &&
              state !== undefined &&
              hasCompleteDurableImageUrls(state)
            ) {
              state.setAiCreateReadyStatus('ready');
            }
          },
      },
      opts.ephemeral !== true,
    ),
  );
}

// ---------------------------------------------------------------------------
// The two stores (one per flow)
// ---------------------------------------------------------------------------

/** Manual flow draft store — persists under `morfi-proposal-form:manual`. */
export const manualProposalFormStore: ProposalFormBoundStore = createProposalFormStore({
  flow: 'manual',
  trackedName: 'useProposalFormStore:manual',
  persistKey: 'morfi-proposal-form:manual',
});

/** AI generate flow draft store — persists under `morfi-proposal-form:ai`. */
export const aiProposalFormStore: ProposalFormBoundStore = createProposalFormStore({
  flow: 'generate',
  trackedName: 'useProposalFormStore:ai',
  persistKey: 'morfi-proposal-form:ai',
});

/** A resumable draft surfaced on step 1 (spec 113 FR-019). */
export interface ProposalDraftSummary {
  title: string;
  flow: 'manual' | 'generate';
}

/**
 * Spec 113 FR-019/FR-020 — pick the single resumable draft for step 1's DRAFT
 * row. A per-flow store is a candidate when its persisted `title` is non-empty.
 * With both present, the most recently edited wins (larger `lastTouchedAt`);
 * ties fall back to a deterministic AI-then-manual preference. Returns null when
 * neither flow holds an in-progress draft.
 */
export function selectResumableDraft(): ProposalDraftSummary | null {
  const manual = manualProposalFormStore.getState();
  const ai = aiProposalFormStore.getState();
  const manualHas = manual.title.trim().length > 0;
  const aiHas = ai.title.trim().length > 0;
  if (!manualHas && !aiHas) return null;
  if (manualHas && !aiHas) return { title: manual.title, flow: 'manual' };
  if (aiHas && !manualHas) return { title: ai.title, flow: 'generate' };
  return ai.lastTouchedAt >= manual.lastTouchedAt
    ? { title: ai.title, flow: 'generate' }
    : { title: manual.title, flow: 'manual' };
}

// ---------------------------------------------------------------------------
// Active-store selection — React context + module-level mirror
// ---------------------------------------------------------------------------

/**
 * Mutable mirror of which bound store the active provider is exposing.
 * Kept in sync by `<ProposalFormStoreProvider>`. Used by the static
 * accessors (`useProposalFormStore.getState()`, `.setState`, `.subscribe`)
 * which run outside React render and so cannot read context.
 *
 * Defaults to the manual store. Tests that don't render a provider read
 * from manual (matches the dialog's default-tab behaviour).
 */
let activeStore: ProposalFormBoundStore = manualProposalFormStore;

const ProposalFormStoreContext: Context<ProposalFormBoundStore> =
  createContext<ProposalFormBoundStore>(manualProposalFormStore);

/**
 * Wrap the proposal-creation dialog tree so all `useProposalFormStore`
 * consumers read from the per-flow store. The provider switches the
 * exposed store synchronously when `flow` flips, so a tab change moves
 * every reader (steps 2-5, AI form, retry buttons, the SSE hook) onto
 * the new flow's draft in a single render pass — no flicker, no leaked
 * state from the other flow.
 */
export function ProposalFormStoreProvider({
  flow,
  children,
  ephemeral = false,
}: {
  ephemeral?: boolean;
  flow: 'manual' | 'generate';
  children: ReactNode;
}): JSX.Element {
  const [sessionStore] = useState(() =>
    ephemeral
      ? createProposalFormStore({
          flow,
          trackedName: 'useProposalFormStore:session',
          persistKey: '',
          ephemeral: true,
        })
      : null,
  );
  const store: ProposalFormBoundStore =
    sessionStore ?? (flow === 'generate' ? aiProposalFormStore : manualProposalFormStore);

  // Publish only committed stores: abandoned renders must not redirect event
  // handlers, and closing an ephemeral form must release its in-memory draft.
  useLayoutEffect(() => {
    const previousStore = activeStore;
    activeStore = store;
    return () => {
      if (activeStore === store) activeStore = previousStore;
    };
  }, [store]);

  // `.ts` (not `.tsx`) so we use `createElement` directly. Equivalent to
  // <ProposalFormStoreContext.Provider value={store}>{children}</…>.
  return createElement(ProposalFormStoreContext.Provider, { value: store }, children);
}

// ---------------------------------------------------------------------------
// Public hook — same callable shape as a Zustand bound store
// ---------------------------------------------------------------------------

/**
 * Internal: hook implementation. Exported callsites use the typed
 * `useProposalFormStore` below (the function-with-statics composite).
 */
/** Module-scoped identity selector — returns the entire state object.
 *  Used as the fallback when `useProposalFormStore()` is called with no
 *  selector. Hoisted (not inline) so its identity is stable across
 *  renders and Zustand's internal `useCallback` deps don't churn. */
const passThroughSelector = (s: ProposalFormState): ProposalFormState => s;

function useProposalFormStoreInner(): ProposalFormState;
function useProposalFormStoreInner<T>(selector: (state: ProposalFormState) => T): T;
function useProposalFormStoreInner<T>(
  selector?: (state: ProposalFormState) => T,
): T | ProposalFormState {
  const boundStore = useContext(ProposalFormStoreContext);
  // The bound Zustand store IS a hook (it wraps `useSyncExternalStore`),
  // but the local `boundStore` variable doesn't start with `use`, so calling
  // it as `boundStore(selector)` looks like a regular function call to
  // React Compiler. The compiler memoised the call on `[selector, boundStore]`,
  // so on a cache hit the underlying `useSyncExternalStore`/`useCallback`s
  // were skipped — the hook count changed between renders and React threw
  // "Should have a queue". Calling `useStore` directly (a name the compiler
  // recognises as a hook) avoids the bad memoisation. The call must be
  // unconditional to satisfy the Rules of Hooks, so the optional-selector
  // overload collapses to a single `useStore` call with a stable identity
  // fallback when no selector is supplied.
  const effectiveSelector = (selector ?? passThroughSelector) as (
    state: ProposalFormState,
  ) => T | ProposalFormState;
  return useStore(boundStore, effectiveSelector);
}

interface UseProposalFormStoreApi {
  (): ProposalFormState;
  <T>(selector: (state: ProposalFormState) => T): T;
  /** Read the active store's current state without subscribing. Mirrors
   *  Zustand's `useStore.getState()`. Outside a provider, points at the
   *  manual store. */
  getState: () => ProposalFormState;
  /** Imperatively patch the active store. Mirrors Zustand's `useStore.setState()`. */
  setState: ProposalFormBoundStore['setState'];
  /** Subscribe to the active store's full state. Mirrors Zustand's
   *  `useStore.subscribe()`. The subscription is tied to whichever store
   *  is active at call time. */
  subscribe: ProposalFormBoundStore['subscribe'];
}

const useProposalFormStoreImpl = useProposalFormStoreInner as UseProposalFormStoreApi;
useProposalFormStoreImpl.getState = (): ProposalFormState => activeStore.getState();
useProposalFormStoreImpl.setState = ((...args: Parameters<ProposalFormBoundStore['setState']>) =>
  activeStore.setState(...args)) as ProposalFormBoundStore['setState'];
useProposalFormStoreImpl.subscribe = ((...args: Parameters<ProposalFormBoundStore['subscribe']>) =>
  activeStore.subscribe(...args)) as ProposalFormBoundStore['subscribe'];

/**
 * Hook for reading the active proposal-form draft store. Inside the
 * dialog tree (wrapped by `<ProposalFormStoreProvider flow={...}>`) this
 * resolves to the per-flow store; outside the dialog (or in tests
 * without a provider) it falls back to the manual store.
 *
 * Same callable shape as a Zustand bound hook:
 *   const state = useProposalFormStore();
 *   const title = useProposalFormStore((s) => s.title);
 *   useProposalFormStore.getState();
 *   useProposalFormStore.setState({ … });
 *   useProposalFormStore.subscribe(…);
 */
export const useProposalFormStore: UseProposalFormStoreApi = useProposalFormStoreImpl;

function configureProposalPersistence(
  initialize: StateCreator<ProposalFormState>,
  options: PersistOptions<ProposalFormState, ProposalFormPersistedState>,
  enabled: boolean,
): StateCreator<ProposalFormState> {
  const configured = enabled ? persist(initialize, options) : initialize;
  return (...args: Parameters<StateCreator<ProposalFormState>>): ProposalFormState =>
    configured(...args);
}
